import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useAuth } from '../context/AuthContext';

interface DocEntry {
  id: string;
  title: string;
  description: string;
  file: string;
  category: string;
}

interface DocsIndex {
  docs: DocEntry[];
}

// Helper to generate slug from heading text
function generateSlug(children: React.ReactNode): string {
  const text = React.Children.toArray(children)
    .map(child => {
      if (typeof child === 'string') return child;
      if (React.isValidElement(child)) {
        const props = child.props as { children?: React.ReactNode };
        if (props.children) {
          return generateSlug(props.children);
        }
      }
      return '';
    })
    .join('');

  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
}

export function Documentation() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { docId } = useParams<{ docId?: string }>();

  const [docsIndex, setDocsIndex] = useState<DocsIndex | null>(null);
  const [content, setContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Load docs index
  useEffect(() => {
    fetch('/docs/index.json')
      .then(res => res.json())
      .then(data => setDocsIndex(data))
      .catch(err => setError('Failed to load documentation index'));
  }, []);

  // Load document content when docId changes
  useEffect(() => {
    if (!docId || !docsIndex) {
      setIsLoading(false);
      return;
    }

    const doc = docsIndex.docs.find(d => d.id === docId);
    if (!doc) {
      setError('Document not found');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');

    fetch(`/docs/${doc.file}`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to load document');
        return res.text();
      })
      .then(text => {
        setContent(text);
        setIsLoading(false);
      })
      .catch(err => {
        setError('Failed to load document');
        setIsLoading(false);
      });
  }, [docId, docsIndex]);

  // Group docs by category
  const docsByCategory = docsIndex?.docs.reduce((acc, doc) => {
    if (!acc[doc.category]) acc[doc.category] = [];
    acc[doc.category].push(doc);
    return acc;
  }, {} as Record<string, DocEntry[]>) || {};

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.logo} onClick={() => navigate('/')}>Uliji</h1>
          <h2 style={styles.pageTitle}>Documentation</h2>
        </div>
        <div style={styles.userInfo}>
          {user && (
            <>
              <span style={styles.username}>{user.username}</span>
              <button onClick={logout} style={styles.logoutButton}>Logout</button>
            </>
          )}
        </div>
      </header>

      <main style={styles.main}>
        {error && <div style={styles.error}>{error}</div>}

        {!docId ? (
          // Index view
          <div style={styles.indexContainer}>
            <h2 style={styles.indexTitle}>Documentation</h2>
            <p style={styles.indexSubtitle}>
              Learn how to use Uliji and build custom trading strategies
            </p>

            {Object.entries(docsByCategory).map(([category, docs]) => (
              <div key={category} style={styles.categorySection}>
                <h3 style={styles.categoryTitle}>{category}</h3>
                <div style={styles.docsGrid}>
                  {docs.map(doc => (
                    <div
                      key={doc.id}
                      style={styles.docCard}
                      onClick={() => navigate(`/docs/${doc.id}`)}
                    >
                      <h4 style={styles.docTitle}>{doc.title}</h4>
                      <p style={styles.docDescription}>{doc.description}</p>
                      <span style={styles.docLink}>Read more →</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Document view
          <div style={styles.documentContainer}>
            <button
              onClick={() => navigate('/docs')}
              style={styles.backButton}
            >
              ← Back to Index
            </button>

            {isLoading ? (
              <div style={styles.loading}>Loading...</div>
            ) : (
              <div style={styles.markdownContent}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    // Custom code block rendering with syntax highlighting
                    code({ node, inline, className, children, ...props }: any) {
                      const match = /language-(\w+)/.exec(className || '');
                      return !inline && match ? (
                        <SyntaxHighlighter
                          style={vscDarkPlus}
                          language={match[1]}
                          PreTag="div"
                          customStyle={{
                            margin: '16px 0',
                            borderRadius: '8px',
                            fontSize: '14px',
                          }}
                          {...props}
                        >
                          {String(children).replace(/\n$/, '')}
                        </SyntaxHighlighter>
                      ) : (
                        <code
                          className={className}
                          style={styles.inlineCode}
                          {...props}
                        >
                          {children}
                        </code>
                      );
                    },
                    // Custom table styling
                    table({ children }: any) {
                      return <table style={styles.table}>{children}</table>;
                    },
                    th({ children }: any) {
                      return <th style={styles.th}>{children}</th>;
                    },
                    td({ children }: any) {
                      return <td style={styles.td}>{children}</td>;
                    },
                    // Custom heading styling with IDs for anchor links
                    h1({ children }: any) {
                      const id = generateSlug(children);
                      return <h1 id={id} style={styles.h1}>{children}</h1>;
                    },
                    h2({ children }: any) {
                      const id = generateSlug(children);
                      return <h2 id={id} style={styles.h2}>{children}</h2>;
                    },
                    h3({ children }: any) {
                      const id = generateSlug(children);
                      return <h3 id={id} style={styles.h3}>{children}</h3>;
                    },
                    // Custom link styling
                    a({ href, children }: any) {
                      // Handle anchor links
                      if (href?.startsWith('#')) {
                        const handleClick = (e: React.MouseEvent) => {
                          e.preventDefault();
                          const targetId = href.slice(1);
                          const element = document.getElementById(targetId);
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            // Update URL hash without jumping
                            window.history.pushState(null, '', href);
                          }
                        };
                        return (
                          <a href={href} onClick={handleClick} style={styles.link}>
                            {children}
                          </a>
                        );
                      }
                      return (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={styles.link}
                        >
                          {children}
                        </a>
                      );
                    },
                    // Horizontal rule
                    hr() {
                      return <hr style={styles.hr} />;
                    },
                    // Blockquote
                    blockquote({ children }: any) {
                      return <blockquote style={styles.blockquote}>{children}</blockquote>;
                    },
                  }}
                >
                  {content}
                </ReactMarkdown>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1a1a2e',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 32px',
    backgroundColor: '#16213e',
    borderBottom: '1px solid #233554',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
  },
  logo: {
    color: '#e94560',
    fontSize: '24px',
    fontWeight: 'bold',
    margin: 0,
    cursor: 'pointer',
  },
  pageTitle: {
    color: '#ccd6f6',
    fontSize: '18px',
    fontWeight: 'normal',
    margin: 0,
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  username: {
    color: '#ccd6f6',
  },
  logoutButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid #e94560',
    backgroundColor: 'transparent',
    color: '#e94560',
    cursor: 'pointer',
    fontSize: '14px',
  },
  main: {
    padding: '32px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  error: {
    backgroundColor: 'rgba(233, 69, 96, 0.2)',
    border: '1px solid #e94560',
    color: '#e94560',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '24px',
  },
  loading: {
    color: '#8892b0',
    textAlign: 'center',
    padding: '48px',
  },
  // Index view styles
  indexContainer: {
    maxWidth: '900px',
    margin: '0 auto',
  },
  indexTitle: {
    color: '#ccd6f6',
    fontSize: '32px',
    marginBottom: '8px',
  },
  indexSubtitle: {
    color: '#8892b0',
    fontSize: '16px',
    marginBottom: '32px',
  },
  categorySection: {
    marginBottom: '32px',
  },
  categoryTitle: {
    color: '#64ffda',
    fontSize: '14px',
    textTransform: 'uppercase',
    letterSpacing: '2px',
    marginBottom: '16px',
  },
  docsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
  },
  docCard: {
    backgroundColor: '#16213e',
    borderRadius: '8px',
    padding: '24px',
    border: '1px solid #233554',
    cursor: 'pointer',
    transition: 'border-color 0.2s, transform 0.2s',
  },
  docTitle: {
    color: '#ccd6f6',
    fontSize: '18px',
    marginTop: 0,
    marginBottom: '8px',
  },
  docDescription: {
    color: '#8892b0',
    fontSize: '14px',
    marginBottom: '16px',
    lineHeight: '1.5',
  },
  docLink: {
    color: '#64ffda',
    fontSize: '14px',
  },
  // Document view styles
  documentContainer: {
    maxWidth: '900px',
    margin: '0 auto',
  },
  backButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid #233554',
    backgroundColor: 'transparent',
    color: '#8892b0',
    cursor: 'pointer',
    fontSize: '14px',
    marginBottom: '24px',
  },
  markdownContent: {
    backgroundColor: '#16213e',
    borderRadius: '12px',
    padding: '32px 48px',
    border: '1px solid #233554',
    color: '#ccd6f6',
    lineHeight: '1.7',
    fontSize: '15px',
  },
  // Markdown element styles
  h1: {
    color: '#ccd6f6',
    fontSize: '28px',
    borderBottom: '1px solid #233554',
    paddingBottom: '12px',
    marginTop: '32px',
    marginBottom: '16px',
  },
  h2: {
    color: '#ccd6f6',
    fontSize: '22px',
    borderBottom: '1px solid #233554',
    paddingBottom: '8px',
    marginTop: '32px',
    marginBottom: '16px',
  },
  h3: {
    color: '#ccd6f6',
    fontSize: '18px',
    marginTop: '24px',
    marginBottom: '12px',
  },
  inlineCode: {
    backgroundColor: '#0a0a14',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '14px',
    color: '#64ffda',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '16px',
    marginBottom: '16px',
  },
  th: {
    backgroundColor: '#0a0a14',
    padding: '12px',
    textAlign: 'left',
    borderBottom: '2px solid #233554',
    color: '#ccd6f6',
    fontSize: '14px',
  },
  td: {
    padding: '12px',
    borderBottom: '1px solid #233554',
    color: '#8892b0',
    fontSize: '14px',
  },
  link: {
    color: '#64ffda',
    textDecoration: 'none',
  },
  hr: {
    border: 'none',
    borderTop: '1px solid #233554',
    margin: '32px 0',
  },
  blockquote: {
    borderLeft: '4px solid #64ffda',
    margin: '16px 0',
    paddingLeft: '16px',
    color: '#8892b0',
    fontStyle: 'italic',
  },
};
